import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AssetDto, DefaultDisplayGuestDto, DisplayUser, GalleryDto, Watcher } from '@nusign/picflow-backend-types';
import { Observable, Subject, catchError, combineLatestWith, debounce, map, of, switchMap, take, tap } from 'rxjs';
import { forkJoinSafe } from '../lib/safe-fork-join';
import { splitName } from '../lib/split-name';
import { AppConfigService } from '../providers/config.service';
import { GalleryPasswordService } from './gallery-password.service';
import { GetAllService } from './get-all.service';
import { GuestAuthService } from './guest-auth.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { StateService } from './state.service';

interface InternalWatcher {
  id: string;
  assetId?: string;
  galleryId?: string;
  user?: string;
}

export interface WatchingPerson {
  id: string;
  first_name: string;
  last_name: string;
  initials: string;
  avatar_url?: string;
  anonymous_id?: string;
}

@Injectable({
  providedIn: 'root',
})
export class WatcherService {
  private config = inject(AppConfigService);
  private http = inject(HttpClient);
  private loader = inject(ObservableDataLoaderService);
  private state = inject(StateService);
  private galleryPasswordService = inject(GalleryPasswordService);
  private getAllService = inject(GetAllService);
  private guestAuthService = inject(GuestAuthService);

  mySubscriptions$ = of(null).pipe(
    switchMap(() =>
      this.loader.get<InternalWatcher[]>(
        'mySubscriptions',
        () =>
          this.getAllService.getAll<Watcher>(`${this.config.apiEndpoint}/watchers`).pipe(
            map(resp => {
              return resp.map(item => ({
                id: item['id'],
                galleryId: item['gallery'] as string,
                assetId: item['asset'] as string,
              }));
            }),
            catchError(() => of([]))
          ),
        1000 * 60 * 5
      )
    )
  );

  private guest$ = this.guestAuthService.onAuthChange.subscribe(guest => {
    if (!guest) return;
    this.loader.forceDataRefresh('mySubscriptions');
  });

  private forceMySubscriptionsRefresh$ = new Subject<void>();
  private forceMySubscriptionsRefreshSubscription = this.forceMySubscriptionsRefresh$
    .pipe(debounce(() => of(10)))
    .subscribe(() => this.loader.forceDataRefresh('mySubscriptions'));

  public watchAsset$ = new Subject<{ assetId: string; galleryId: string }>();
  private watchedAssets$ = this.watchAsset$.subscribe(({ assetId, galleryId }) => {
    if (this.state.guest && !this.state.guest.email) return of(undefined);
    return this.mySubscriptions$
      .pipe(
        take(1),
        switchMap(items => {
          if (items.find(item => item.galleryId === galleryId)) return of(undefined);
          if (items.find(item => item.assetId === assetId)) return of(undefined);
          return this.http
            .post<void>(`${this.config.apiEndpoint}/watchers${this.getPassword(galleryId)}`, {
              asset: assetId,
            })
            .pipe(map(() => true));
        }),
        tap(resp => {
          if (!resp) return;
          this.forceMySubscriptionsRefresh$.next();
          this.loader.forceDataRefresh(`assetWatchers-${assetId}`);
        })
      )
      .subscribe();
  });

  public watchGallery$ = new Subject<string>();
  private watchedGalleries$ = this.watchGallery$.subscribe(galleryId => {
    if (this.state.guest && !this.state.guest.email) return of(undefined);
    return this.mySubscriptions$
      .pipe(
        take(1),
        switchMap(items => {
          if (items.find(item => item.galleryId === galleryId)) return of(undefined);
          return this.http
            .post<void>(`${this.config.apiEndpoint}/watchers${this.getPassword(galleryId)}`, {
              gallery: galleryId,
            })
            .pipe(map(() => true));
        }),
        tap(resp => {
          if (!resp) return;
          this.forceMySubscriptionsRefresh$.next();
          this.loader.forceDataRefresh(`galleryWatchers-${galleryId}`);
        })
      )
      .subscribe();
  });

  public unWatchAsset$ = new Subject<string>();
  private unWatchedAssets$ = this.unWatchAsset$.subscribe(assetId => {
    if (this.state.guest && !this.state.guest.email) return of(undefined);
    return this.mySubscriptions$
      .pipe(
        take(1),
        switchMap(items => {
          const item = items.find(item => item.assetId === assetId);
          if (!item) return of(undefined);
          return this.http.delete(`${this.config.apiEndpoint}/watchers/${item.id}`).pipe(map(() => true));
        }),
        tap(resp => {
          if (!resp) return;
          this.loader.update<InternalWatcher[]>('mySubscriptions', items => {
            if (!items) return;
            return items.filter(item => item.assetId !== assetId);
          });

          this.loader.update<Watcher[]>(`assetWatchers-${assetId}`, items => {
            if (!items) return;
            return items.filter(item => (item.asset as AssetDto)?.id !== assetId);
          });
        })
      )
      .subscribe();
  });

  public unWatchGallery$ = new Subject<string>();
  private unWatchedGalleries$ = this.unWatchGallery$.subscribe(galleryId => {
    return this.mySubscriptions$
      .pipe(
        take(1),
        switchMap(items => {
          const _items = items.filter(item => item.galleryId === galleryId);
          if (!_items.length) return of(undefined);
          return forkJoinSafe(
            _items.map(item => this.http.delete(`${this.config.apiEndpoint}/watchers/${item.id}`))
          ).pipe(map(() => true));
        }),
        tap(resp => {
          if (!resp) return;
          this.loader.update<InternalWatcher[]>('mySubscriptions', items => {
            if (!items) return;
            return items.filter(item => item.galleryId !== galleryId);
          });

          this.loader.update<Watcher[]>(`galleryWatchers-${galleryId}`, items => {
            if (!items) return;
            return items.filter(item => (item.gallery as GalleryDto)?.id !== galleryId);
          });
        })
      )
      .subscribe();
  });

  public getAssetWatchers(assetId: string, galleryId: string): Observable<WatchingPerson[]> {
    return this.internalGetAssetWatchers(assetId, galleryId).pipe(
      combineLatestWith(this.internalGetGalleryWatchers(galleryId)),
      map(([assetWatchers, galleryWatchers]) => {
        const list: Watcher[] = assetWatchers;
        // Merge gallery watchers into asset watchers because they are also watching the asset
        galleryWatchers.forEach(watcher => {
          if (watcher.user || watcher.guest) {
            if (watcher.user) {
              if (watcher.user && list.find(i => (i.user as DisplayUser)?.id === (watcher.user as DisplayUser)?.id))
                return;
            } else {
              if (
                watcher.guest &&
                list.find(
                  i => (i.guest as DefaultDisplayGuestDto)?.id === (watcher.guest as DefaultDisplayGuestDto)?.id
                )
              )
                return;
            }
          }
          list.push(watcher);
        });
        const people: (DisplayUser | DefaultDisplayGuestDto)[] = list.map(watcher => {
          return 'user' in watcher && watcher.user
            ? (watcher.user as DisplayUser)
            : (watcher.guest as DefaultDisplayGuestDto);
        });

        const mappedPeople: WatchingPerson[] = [];

        people.forEach(watcher => {
          if (!watcher) return;
          const first_name = 'first_name' in watcher ? watcher.first_name : this.getFirstName(watcher);
          const last_name = 'last_name' in watcher ? watcher.last_name : this.getLastName(watcher);

          const mappedWatcher: WatchingPerson = {
            first_name,
            last_name,
            initials: this.getInitials(first_name, last_name),
            id: watcher.id,
          };

          if ('name' in watcher) {
            mappedWatcher.anonymous_id = watcher.anonymous_id;
          } else {
            mappedWatcher.avatar_url = watcher.avatar_url;
          }
          mappedPeople.push(mappedWatcher);
        });

        return mappedPeople;
      })
    );
  }

  private getFirstName(watcher: DefaultDisplayGuestDto) {
    const { first_name } = splitName(watcher.name);
    return first_name;
  }

  private getLastName(watcher: DefaultDisplayGuestDto) {
    const { last_name } = splitName(watcher.name);
    return last_name;
  }

  private getInitials(first_name: string, last_name: string): string {
    let initials = first_name[0];
    if (last_name) initials += last_name[0];
    return initials;
  }

  private internalGetAssetWatchers(assetId: string, galleryId: string) {
    return this.loader.get<Watcher[]>(
      `assetWatchers-${assetId}`,
      () => {
        const url = `${this.config.apiEndpoint}/assets/${assetId}/watchers${this.getPassword(galleryId)}`;
        return this.http.get<{ data: Watcher[] }>(url).pipe(map(resp => resp.data));
      },
      1000 * 60 * 1
    );
  }

  private internalGetGalleryWatchers(galleryId: string) {
    return this.loader.get<Watcher[]>(
      `galleryWatchers-${galleryId}`,
      () => {
        const url = `${this.config.apiEndpoint}/galleries/${galleryId}/watchers${this.getPassword(galleryId)}`;
        return this.http.get<{ data: Watcher[] }>(url).pipe(map(resp => resp.data));
      },
      1000 * 60 * 1
    );
  }

  private getPassword(galleryId: string) {
    const password = this.galleryPasswordService.getPassword(galleryId);
    if (!password) return '';
    return '?password=' + encodeURIComponent(password);
  }
}
